/**
 * Build the download name for a per-page export.
 *
 * UI-41: these exports were named after the document's UUID, so a saved file
 * read `a3f21c88-9d4e-...._page_1_raw.txt` and was unrecognisable in a
 * Downloads folder. The document's own name is what a person recognises.
 *
 * Nothing parses these names back — grepping `_raw.txt` / `_page_` across the
 * frontend and both backends finds no reader — so they are display-only and
 * safe to change.
 */

/** Windows forbids these outright; the rest are just noise in a filename. */
const UNSAFE = /[\\/:*?"<>|]/g

/**
 * Only KNOWN source extensions are stripped (greptile on #31189). Display
 * names are free-form, so treating every final dotted segment as an extension
 * loses real information: "P&ID Rev.2" would become "P&ID Rev" and
 * "Process version.3" would lose its version. An allowlist cannot make that
 * mistake — an unrecognised suffix is kept.
 */
const SOURCE_EXTENSIONS = new Set([
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'txt', 'csv', 'tsv', 'json', 'xml', 'rtf',
  'png', 'jpg', 'jpeg', 'gif', 'bmp', 'tif', 'tiff', 'svg', 'webp',
  'dwg', 'dxf', 'zip',
])

/** Keep the stem readable but bounded — the OS truncates long names anyway. */
const MAX_STEM = 80

export function exportFileStem(name: string | null | undefined): string | null {
  if (!name) return null
  // Drop a trailing SOURCE extension: the export is .txt, so
  // "report.pdf_page_1_raw.txt" reads like a mistake. A suffix that is not a
  // known extension ("Rev.2") is information and is kept.
  const match = /\.([^./\\]+)$/.exec(name)
  const withoutExt =
    match && SOURCE_EXTENSIONS.has(match[1].toLowerCase())
      ? name.slice(0, match.index)
      : name
  const cleaned = withoutExt.replace(UNSAFE, '_').replace(/\s+/g, ' ').trim()
  if (!cleaned) return null
  return cleaned.length > MAX_STEM ? cleaned.slice(0, MAX_STEM).trim() : cleaned
}

/**
 * `fileName` is the document's display name; `fallback` is used when it is
 * missing or sanitises away to nothing.
 *
 * The export is named after the document ALONE — "dummy.docx" exports as
 * "dummy.txt". No page number and no "_raw" marker, by product decision.
 *
 * Consequence, recorded deliberately: every page of a document exports to the
 * same name, so saving page 2 after page 1 does not overwrite silently but
 * does land as "dummy (1).txt" with nothing in the name saying which page it
 * is. That trade was chosen over a longer, more literal filename.
 */
export function rawExportFileName(
  fileName: string | null | undefined,
  fallback = 'document',
): string {
  const stem = exportFileStem(fileName) ?? fallback
  return `${stem}.txt`
}
